import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Accordion, Button, Form, Modal, Badge, Spinner, Alert } from 'react-bootstrap';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ccmService from '../../services/ccmService';
import './CarePlans.css';

const CarePlans = () => {
  const { enrollmentId } = useParams();
  const [searchParams] = useSearchParams();
  const virtualVisitId = searchParams.get('visitId');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [carePlans, setCarePlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCarePlan, setNewCarePlan] = useState({
    template_id: '',
    plan_name: 'Comprehensive Care Plan',
    plan_content: 'Standard care plan framework',
    next_review_date: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
    goals: [
      {
         goal_description: '',
         objectives: [],
         interventions: [],
         priority: 'medium',
         status: 'not_started'
      }
    ]
  });

  useEffect(() => {
    if (enrollmentId) {
      fetchCarePlans(enrollmentId);
      fetchTemplates();
    } else {
      setLoading(false);
    }
  }, [enrollmentId]);

  const fetchCarePlans = async (eid) => {
    try {
      setLoading(true);
      const response = await ccmService.getCarePlans(eid);
      if (response.success) {
        // Parse goals if they are strings
        const plans = response.data.map(p => ({
          ...p,
          goals: p.goals ? p.goals.map(g => ({
             ...g,
             objectives: typeof g.objectives === 'string' ? JSON.parse(g.objectives) : g.objectives || [],
             interventions: typeof g.interventions === 'string' ? JSON.parse(g.interventions) : g.interventions || []
          })) : []
        }));
        setCarePlans(plans);
      }
    } catch (error) {
      console.error('Error fetching care plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await ccmService.getCarePlanTemplates('', 'CCM');
      if (response.success) {
        setTemplates(response.data || []);
      }
    } catch (error) {}
  };

  const handleAddGoal = () => {
    setNewCarePlan({
      ...newCarePlan,
      goals: [...newCarePlan.goals, { goal_description: '', objectives: [], interventions: [], priority: 'medium', status: 'not_started' }]
    });
  };

  const handleUpdateGoal = (index, field, value) => {
    const updatedGoals = [...newCarePlan.goals];
    updatedGoals[index][field] = value;
    setNewCarePlan({...newCarePlan, goals: updatedGoals});
  };

  const handleArrayField = (goalIndex, field, value, itemIndex) => {
    const updatedGoals = [...newCarePlan.goals];
    if (itemIndex === -1) {
       // add new
       updatedGoals[goalIndex][field].push(value);
    } else {
       updatedGoals[goalIndex][field][itemIndex] = value;
    }
    setNewCarePlan({...newCarePlan, goals: updatedGoals});
  };

  const handleCreateCarePlan = async () => {
    try {
      const planData = {
        ...newCarePlan,
        enrollment_id: enrollmentId,
        virtual_visit_id: virtualVisitId,
        plan_content: typeof newCarePlan.plan_content === 'string' 
          ? newCarePlan.plan_content 
          : JSON.stringify(newCarePlan.plan_content)
      };
      
      const response = await ccmService.createCarePlan(planData);
      if (response.success) {
        setShowCreateModal(false);
        fetchCarePlans(enrollmentId);
      }
    } catch (error) {
      alert('Failed to create care plan');
    }
  };

  const handleApprove = async (carePlanId) => {
    if (window.confirm('Are you sure you want to approve and sign this care plan?')) {
      try {
        const response = await ccmService.approveCarePlan(carePlanId);
        if (response.success) fetchCarePlans(enrollmentId);
      } catch (error) {
        alert('Failed to approve care plan');
      }
    }
  };

  if (loading) {
    return (
      <Container fluid className="ccm-cp-container pt-5 text-center">
        <Spinner animation="grow" variant="primary" />
        <p className="mt-3 text-muted">Loading Care Plans...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="ccm-cp-container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
           <Button variant="link" className="text-decoration-none p-0 mb-2" onClick={() => navigate(`/ccm/phm-hub/${enrollmentId}`)}>
              <i className="bi bi-arrow-left"></i> Back to PHM Hub
           </Button>
           <h3 className="fw-bold">Comprehensive Care Plans</h3>
           {virtualVisitId && <Badge bg="success">Linked to Virtual Visit: #{virtualVisitId}</Badge>}
        </div>
        
        {enrollmentId && (
          <Button variant="primary" className="premium-btn shadow-sm" onClick={() => setShowCreateModal(true)}>
            <i className="bi bi-plus-circle me-2"></i> Document New Care Plan
          </Button>
        )}
      </div>

      {!enrollmentId ? (
        <Alert variant="warning">Please select an enrollment to view care plans</Alert>
      ) : (
        <Row className="g-4">
           {carePlans.length === 0 && (
             <Col xs={12}>
                <Card className="text-center p-5 border-0 shadow-sm bg-light">
                   <h5 className="text-muted">No Care Plans Documented</h5>
                   <p className="text-muted small">Use the button above to generate a new Care Plan for this patient.</p>
                </Card>
             </Col>
           )}
           {carePlans.map((plan) => (
             <Col md={12} key={plan.id}>
                <Card className="border-0 shadow-sm care-plan-card">
                  <Card.Header className="bg-white border-0 d-flex justify-content-between pt-4 pb-2">
                     <div>
                        <h5 className="fw-bold mb-1">{plan.plan_name} <Badge bg={plan.status === 'active' ? 'success' : plan.status === 'draft' ? 'warning' : 'secondary'} className="ms-2">{plan.status.toUpperCase()}</Badge></h5>
                        <p className="text-muted small mb-0">Created on {new Date(plan.created_date).toLocaleDateString()} | Next Review: {plan.next_review_date ? new Date(plan.next_review_date).toLocaleDateString() : 'N/A'}</p>
                     </div>
                     <div>
                        {plan.status === 'draft' && (
                          <Button variant="success" className="me-2 premium-btn btn-success" onClick={() => handleApprove(plan.id)}>
                            <i className="bi bi-pencil-fill me-1"></i> Sign & Approve
                          </Button>
                        )}
                        <Button variant="outline-primary"><i className="bi bi-printer"></i></Button>
                     </div>
                  </Card.Header>
                  <Card.Body>
                     <h6 className="fw-bold text-primary mb-3">Problems & Goals</h6>
                     <Accordion>
                        {plan.goals && plan.goals.map((g, idx) => (
                          <Accordion.Item eventKey={idx.toString()} key={g.id}>
                            <Accordion.Header>
                               <Badge bg={g.priority==='high' ? 'danger' : 'info'} className="me-2">{g.priority}</Badge>
                               <strong>Goal: </strong> {g.goal_description}
                            </Accordion.Header>
                            <Accordion.Body className="bg-light">
                               <Row>
                                 <Col md={6}>
                                   <h6 className="fw-bold text-secondary">Objectives</h6>
                                   <ul>
                                     {g.objectives && g.objectives.length > 0 ? g.objectives.map((obj, i) => <li key={i}>{obj}</li>) : <li className="text-muted">No objectives specified</li>}
                                   </ul>
                                 </Col>
                                 <Col md={6}>
                                   <h6 className="fw-bold text-secondary">Interventions</h6>
                                   <ul>
                                     {g.interventions && g.interventions.length > 0 ? g.interventions.map((intv, i) => <li key={i}>{intv}</li>) : <li className="text-muted">No interventions specified</li>}
                                   </ul>
                                 </Col>
                               </Row>
                            </Accordion.Body>
                          </Accordion.Item>
                        ))}
                     </Accordion>
                  </Card.Body>
                </Card>
             </Col>
           ))}
        </Row>
      )}

      {/* Create Care Plan Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="xl" backdrop="static">
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold text-primary">Document Comprehensive Care Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted fw-bold">Plan Title *</Form.Label>
                  <Form.Control
                    type="text"
                    value={newCarePlan.plan_name}
                    className="premium-input-light"
                    onChange={(e) => setNewCarePlan({ ...newCarePlan, plan_name: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted fw-bold">Next Review Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newCarePlan.next_review_date}
                    className="premium-input-light"
                    onChange={(e) => setNewCarePlan({ ...newCarePlan, next_review_date: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <hr className="my-4 text-muted" />
            <h5 className="fw-bold mb-3">Problems & Structured Goals</h5>

            {newCarePlan.goals.map((goal, idx) => (
               <Card key={idx} className="mb-4 border-primary shadow-sm">
                 <Card.Header className="d-flex justify-content-between align-items-center bg-white">
                    <span className="fw-bold">Problem #{idx + 1}</span>
                 </Card.Header>
                 <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Goal Description</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="E.g., Reduce HbA1c below 7%"
                        value={goal.goal_description}
                        onChange={(e) => handleUpdateGoal(idx, 'goal_description', e.target.value)}
                      />
                    </Form.Group>
                    <Row>
                      <Col md={6}>
                         <Form.Group className="mb-3">
                           <Form.Label>Add Objective</Form.Label>
                           <div className="d-flex gap-2 mb-2">
                              <Form.Control type="text" id={`obj-input-${idx}`} placeholder="E.g., Patient exercises 3x week" />
                              <Button variant="outline-primary" onClick={() => {
                                 const val = document.getElementById(`obj-input-${idx}`).value;
                                 if(val) { handleArrayField(idx, 'objectives', val, -1); document.getElementById(`obj-input-${idx}`).value=''; }
                              }}>Add</Button>
                           </div>
                           <ul className="mb-0 text-muted small">
                             {goal.objectives.map((obj, oIdx) => <li key={oIdx}>{obj}</li>)}
                           </ul>
                         </Form.Group>
                      </Col>
                      <Col md={6}>
                          <Form.Group className="mb-3">
                           <Form.Label>Add Intervention</Form.Label>
                           <div className="d-flex gap-2 mb-2">
                              <Form.Control type="text" id={`intv-input-${idx}`} placeholder="E.g., Educate on diabetic diet" />
                              <Button variant="outline-primary" onClick={() => {
                                 const val = document.getElementById(`intv-input-${idx}`).value;
                                 if(val) { handleArrayField(idx, 'interventions', val, -1); document.getElementById(`intv-input-${idx}`).value=''; }
                              }}>Add</Button>
                           </div>
                           <ul className="mb-0 text-muted small">
                             {goal.interventions.map((intv, iIdx) => <li key={iIdx}>{intv}</li>)}
                           </ul>
                         </Form.Group>
                      </Col>
                    </Row>
                 </Card.Body>
               </Card>
            ))}

            <Button variant="outline-secondary" className="w-100 border-dashed py-3" onClick={handleAddGoal}>
               <i className="bi bi-plus-circle me-2"></i> Add Another Problem/Goal
            </Button>
          </Form>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel Documenting</Button>
          <Button variant="primary" className="premium-btn px-4" onClick={handleCreateCarePlan}>Generate Care Plan Draft</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CarePlans;
